/* eslint-disable no-restricted-syntax */
/* eslint-disable no-use-before-define */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation, Translation } from 'react-i18next';
import ShipmentWebAPIUtils, {
  podEmailOnPodUpload,
} from 'utils/ShipmentWebAPIUtils';
import {
  BARCODE,
  carrierViewableDocTypesMap,
  DELIVERY_ORDER,
  INVOICE,
  PROOF_OF_DELIVERY,
  PAYOUT,
  shipperViewableDocTypesMap,
} from 'config/documents';
import {
  useCombineDocuments,
  useGetDocuments,
  useHandleDeleteDocuments,
  useHandleUploadDocuments,
  useProcessShipment,
} from 'utils/DocumentsHooks';
import SessionStore from 'stores/SessionStore';
import { showToast } from 'stores/ToastStore';
import {
  attachDocuments,
  RecognitionFieldTypes,
  waitDocumentUpload,
} from 'utils/DocumentsAPIUtils';
import Pagination from 'components/v2/PaginationV2';
import MultiSelectDocumentType from 'components/site/multi-document-type-select';
import { DocumentsUploader } from './DocumentsUploader';
import { DocumentsList } from './DocumentsList';
import { DOCUMENT_STATUSES } from './Document';
import Alert from 'components/ui/Alert';

const DOCUMENTS_PER_PAGE = 10;

export function Documents({
  isOrder,
  carrierDocumentUpload,
  shipment,
  stop,
  showDropzone = true,
  deleteDisabled,
  onDelete,
  onDeleteFailed,
  onDeleteDecline,
  onUpload,
  onUploadFailed,
  refetchRef,
  shipments,
  isStop,
  stopShipmentId,
  pageNeighbours,
}) {
  const { t } = useTranslation();
  const [documents, setDocuments] = useState([]);
  const [failedOrPendingDocuments, setFailedOrPendingDocuments] = useState([]);
  const [foreignKeys, setForeignKeys] = useState([shipment._id]);
  const [sentDocuments, setSentDocuments] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const [page, setPage] = useState(1);
  const shipmentId = shipment?._id;
  const [filterDocumentTypes, setFilterDocumentTypes] = useState([]);
  const [refetchDocumentFilterType, setRefetchDocumentFilterType] =
    useState(false);

  useEffect(() => {
    const sentAudits = shipment?.audit?.filter(
      (audit) => audit.event === 'do_sent',
    );
    const sentDocumentsUri = sentAudits?.map((audit) => audit.data.uri);
    const sentDocuments = documents.filter((doc) =>
      sentDocumentsUri?.includes(doc.key),
    );
    setSentDocuments(sentDocuments);
  }, [shipmentId]);

  const userRole = useMemo(() => {
    const user = SessionStore.getUser();
    return user._type;
  }, []);

  const excludedDocTypes = [BARCODE.value];

  const includedDocTypes = useMemo(() => {
    if (!userRole) {
      return [];
    }

    const userType = SessionStore.getUserType();
    let viewableDocsTypes = [];

    switch (userType) {
      case 'Shipper':
        viewableDocsTypes = Object.keys(shipperViewableDocTypesMap);
        break;
      case 'Carrier':
        viewableDocsTypes = Object.keys(carrierViewableDocTypesMap);
        break;
      default:
        viewableDocsTypes = [];
        break;
    }

    return viewableDocsTypes;
  }, []);

  const groupDocTypes = useMemo(() => {
    const userType = SessionStore.getUserType();
    const groupDocTypes = [];

    if (userType === 'Shipper' || isOrder) {
      groupDocTypes.push(INVOICE.value);
    }

    if (userType === 'Carrier' || isOrder) {
      groupDocTypes.push(PAYOUT.value);
    }

    if (['Carrier', 'Shipper'].includes(userRole)) {
      groupDocTypes.push(DELIVERY_ORDER.value);
    }

    return groupDocTypes;
  }, []);

  const {
    data: documentsData,
    error: documentError,
    refetch: documentsRefetch,
    loading,
  } = useGetDocuments({
    foreignKeys,
    type:
      filterDocumentTypes.length > 0 ? filterDocumentTypes : includedDocTypes,
    excludeTypes: excludedDocTypes,
    groupTypes: groupDocTypes,
    options: {
      limit: DOCUMENTS_PER_PAGE,
      skip: (page - 1) * DOCUMENTS_PER_PAGE,
      order: [{ field: 'createdAt', direction: 'desc' }],
    },
    fetchPolicy: 'network-only',
  });

  // Getting all documents without pagination for deciding whether to show Warning banner for Failed/ Pending Documents
  const { data: allDocumentsData, loading: allDocumentsLoading } =
    useGetDocuments({
      foreignKeys,
      type:
        filterDocumentTypes.length > 0 ? filterDocumentTypes : includedDocTypes,
      excludeTypes: excludedDocTypes,
      groupTypes: groupDocTypes,
      options: {
        limit: 1000,
        skip: 0,
        order: [{ field: 'createdAt', direction: 'desc' }],
      },
      fetchPolicy: 'network-only',
    });

  const showGetDocumentsErrorToast = (err) => {
    showToast(
      t('GET_DOCUMENT_ERROR'),
      t('GET_DOCUMENT_FAILED_WITH_ERROR', { error: err }),
      'error',
    );
  };

  const showDocumentDeletedToast = () => {
    showToast(t('DOCUMENT_DELETE_SUCCEED'), null);
  };

  const getPayoutIds = (shipment) => {
    return shipment.payables?.map((payable) => payable.payout_id) || [];
  };

  const getInvoiceIds = (shipment) => {
    return (
      shipment.receivables?.map((receivable) => receivable.invoice_id) || []
    );
  };

  useEffect(() => {
    if (isOrder) {
      return;
    }

    const keys = [
      shipment._id,
      ...getPayoutIds(shipment),
      ...getInvoiceIds(shipment),
    ];

    setForeignKeys([...new Set(keys)].filter((key) => key));
  }, [
    isOrder,
    shipmentId,
    shipment.payables?.length,
    shipment.receivables?.length,
  ]);

  useEffect(() => {
    if (!isOrder) {
      return;
    }

    const allShipmentKeys = shipments
      ?.map((shipment) => [
        ...getPayoutIds(shipment),
        ...getInvoiceIds(shipment),
      ])
      .flat();

    const keys = [shipment._id, ...allShipmentKeys];

    setForeignKeys([...new Set(keys)].filter((key) => key));
  }, [isOrder, shipments?.length]);

  const [processDocument] = useProcessShipment();
  const [combineDocuments] = useCombineDocuments();
  const refetchTimeoutRef = useRef(0);

  const refetchHandler = useCallback(() => {
    documentsRefetch();
  }, [documentsRefetch]);

  const { handleUpload } = useHandleUploadDocuments(
    shipment,
    stop,
    async (shipment, stop, type, uploadedData, userVisibility) => {
      if (type === PROOF_OF_DELIVERY.value) {
        let combinedDocument = await combineDocuments(
          uploadedData.map((document) => document.id),
          type,
          PROOF_OF_DELIVERY.name,
        );
        await waitDocumentUpload(combinedDocument.data.key);
        await attachDocuments(combinedDocument.data.id, [shipment.id]);
        await processDocument(combinedDocument.data.id, shipment.id, stop?.id, {
          recognitionFieldsType: RecognitionFieldTypes.GENERIC_UPLOAD_POD,
        });
        uploadedData = [combinedDocument.data];
        if (shipment.shipment_id)
          podEmailOnPodUpload(shipment, uploadedData[0]);
      }

      await documentsRefetch();
      setRefetchDocumentFilterType(true);

      if (onUpload) {
        return onUpload(shipment, stop, type, uploadedData, userVisibility);
      }
    },
    onUploadFailed,
  );

  const deleteFailed = async (documents, e) => {
    console.error(e);

    showToast(
      <Translation>{(t) => <p>{t('DOCUMENT_DELETE_FAILED')}</p>}</Translation>,
      <Translation>{(t) => <p>{t('DOCUMENT_DELETE_FAILED')}</p>}</Translation>,
      'error',
    );

    return onDeleteFailed?.(documents, e);
  };

  const deleteDocuments = async (documentIds) => {
    const response = await ShipmentWebAPIUtils.deleteStopDocumentsV2(
      stopShipmentId,
      shipment,
      documentIds,
    ).catch((e) => {
      throw e;
    });
    return response;
  };

  const handleStopDocumentDelete = async (documents) => {
    try {
      setDeleting(true);
      const documentIds = documents.map((document) => document.id);
      await deleteDocuments(documentIds);
      showDocumentDeletedToast();
      documentsRefetch();
      setRefetchDocumentFilterType(true);
      return onDelete(documents);
    } catch (e) {
      return deleteFailed(documents, e);
    } finally {
      setDeleting(false);
    }
  };

  const { handleDelete } = useHandleDeleteDocuments((documents) => {
    showDocumentDeletedToast();
    // update doc list after delete
    documentsRefetch();
    setRefetchDocumentFilterType(true);
    onDelete?.(documents);
  }, onDeleteFailed);

  const handleDocumentDelete = (documents) => {
    return isStop
      ? handleStopDocumentDelete(documents)
      : handleDelete(documents);
  };

  useEffect(() => {
    refetchRef?.(refetchHandler);

    return () => {
      refetchRef?.(refetchHandler);
    };
  }, [shipmentId, documentsRefetch]);

  useEffect(() => {
    if (documentError) {
      showGetDocumentsErrorToast(documentError);
    }
  }, [documentError]);

  useEffect(() => {
    documentsRefetch();
  }, [filterDocumentTypes]);

  useEffect(() => {
    const viewableDocuments = documentsData?.documents || [];

    setDocuments(viewableDocuments);

    const hasPendingDocuments = !!viewableDocuments.find(
      (document) => document.status === DOCUMENT_STATUSES.PENDING,
    );

    if (hasPendingDocuments) {
      clearTimeout(refetchTimeoutRef.current);

      refetchTimeoutRef.current = setTimeout(() => {
        refetchTimeoutRef.current = 0;
        refetchHandler();
      }, 10000);
    }
  }, [documentsData]);

  useEffect(() => {
    if (!allDocumentsLoading && allDocumentsData) {
      const allDocuments = allDocumentsData?.documents || [];

      const filteredDocuments = allDocuments.filter(
        (document) =>
          document.status === DOCUMENT_STATUSES.PENDING ||
          document.status === DOCUMENT_STATUSES.UPLOAD_FAILED,
      );
      setFailedOrPendingDocuments(filteredDocuments);
    }
  }, [allDocumentsData, allDocumentsLoading]);

  const pageCount = Math.ceil((documentsData?.count || 0) / DOCUMENTS_PER_PAGE);

  if (pageCount && page > pageCount) {
    setPage(pageCount);
  }

  return (
    <div id="shipment-document-upload-zone">
      <div className="sm:w-1/2 lg:w-1/2 w-full">
        <MultiSelectDocumentType
          shipmentId={shipment._id}
          filterDocumentTypes={filterDocumentTypes}
          setFilterDocumentTypes={setFilterDocumentTypes}
          excludedDocTypes={excludedDocTypes}
          includedDocTypes={includedDocTypes}
          refetch={refetchDocumentFilterType}
          setRefetch={setRefetchDocumentFilterType}
        />
      </div>
      {!allDocumentsLoading && failedOrPendingDocuments.length > 0 && (
        <Alert type="warning" className="w-full mb-4">
          {t('DOCUMENTS_IN_FAILED_PENDING_STATUS')}
        </Alert>
      )}
      <DocumentsList
        isOrder={isOrder}
        documents={documents}
        deleteDisabled={deleteDisabled}
        onDeleteConfirm={(document) => handleDocumentDelete([document])}
        onDeleteDecline={onDeleteDecline}
        sentDocuments={sentDocuments}
        loading={loading}
      />
      <Pagination
        className="border-t-0"
        perPage={DOCUMENTS_PER_PAGE}
        page={page}
        pages={pageCount}
        total={documentsData?.count}
        showCount
        onPageClick={(page) => setPage(page)}
        pageNeighbours={pageNeighbours}
      />
      <hr />
      {showDropzone && (
        <DocumentsUploader
          parentType={isOrder ? 'order' : 'shipment'}
          shipment={shipment}
          carrierDocumentUpload={carrierDocumentUpload}
          onUpload={handleUpload}
        />
      )}
    </div>
  );
}
